import { Mixins, Component, Watch } from 'vue-property-decorator'
import { CollectionsService, PointsOfInterestService, MediasService, DatasetService } from '@/@next/services'
import {
    Collection, 
    MediaAudio,
    Maybe,
    MediaVideo,
    MediaImage,
    PointOfInterest,
    Content,
    ImageContent,
    QuestionContent,
    I18nRef,
    AudioGuideTrack,
    AudioContent
} from '@/@types/graphql'
import addProtocol from '@/utils/addProtocol'
import { CLIENT, CACHE_STRATEGY } from '@/constants';
import * as APP from '@/@next/store/app/types'
import { Getter, Mutation, Action } from 'vuex-class'
import { smallerImageUrl } from '@/@next/helpers/imageFormat'
import { Image } from 'konva/types/shapes/Image';

type cachableItemTypes = Collection | PointOfInterest | MediaImage | MediaVideo | MediaAudio

@Component({ name: 'preload-service' })
export default class Preload extends Mixins(
    CollectionsService,
    PointsOfInterestService,
    MediasService,
    DatasetService) {

    urlList: string[] = [];

    @Getter(APP.PRELOAD_DATASET) preloadDataset!: any
    @Mutation(APP.PRELOAD_DATASET) setPreloadDataset!: any
    
    @Getter(APP.APP_IS_READY) appIsReady!: boolean

    @Getter(APP.SERVICE_WORKER_IS_READY) serviceWorkerIsReady!: boolean

    @Getter(APP.PRELOAD_STATUS) preloadStatus!: string
    @Mutation(APP.PRELOAD_STATUS) setPreloadStatus!: any

    @Getter(APP.PRELOADING) preloading!: boolean
    @Getter(APP.DISPLAY_PRELOAD_STATUS) displayPreloadStatus!: boolean
    @Mutation(APP.DISPLAY_PRELOAD_STATUS) setDisplayPreloadStatus!: any

    @Getter(APP.ACCEPT_PRELOAD) acceptPreload!: boolean 
    @Mutation(APP.ACCEPT_PRELOAD) setAcceptPreload!: any

    @Getter(APP.PRELOAD_ENDED) preloadEnded!: boolean

    @Getter(APP.PRELOAD_SUCCESS) preloadSuccess!: boolean
    @Mutation(APP.PRELOAD_SUCCESS) setPreloadSuccess!: any

    @Getter(APP.PRELOAD_ERROR) preloadError!: boolean
    @Mutation(APP.PRELOAD_ERROR) setPreloadError!: any

    @Getter(APP.NEED_TO_RELOAD_APP) needToReloadApp!: boolean
    @Mutation(APP.NEED_TO_RELOAD_APP) setNeedToReloadApp!: any
    
    async fetchPreloadDataset () {
        try {
            const res = await this.GET_DATASET('preloadResources')
            if (!res) return
            this.setPreloadDataset(res.value)
        } catch (e) {
            console.error(`Error fetching preload dataset: ${e}`)
        }
    }

    get preloadIsEnabled (): boolean {
        return CACHE_STRATEGY === 'sw-and-preload'
    }

    multipleItemQuery (typename: string, take: number) {
        switch (typename) {
            case 'MediaImage': return take > -1 ? this.GET_ALL_IMAGE({ take }) : this.GET_ALL_IMAGE()
            case 'MediaVideo': return take > -1 ? this.GET_ALL_VIDEO({ take }) : this.GET_ALL_VIDEO()
            case 'MediaAudio': return take > -1 ? this.GET_ALL_AUDIO({ take }) : this.GET_ALL_AUDIO()
            case 'Collection': return take > -1 ? this.GET_ALL_COLLECTIONS({ take }) : this.GET_ALL_COLLECTIONS()
            case 'PointOfInterest': return take > -1 ? this.GET_ALL_POIS({ take }) : this.GET_ALL_POIS()
        }
    }

    singleItemQuery (id: string, typename: string) {
        switch (typename) {
            case 'MediaImage': return this.GET_SINGLE_IMAGE(id)
            case 'MediaVideo': return this.GET_SINGLE_VIDEO(id)
            case 'MediaAudio': return this.GET_SINGLE_AUDIO(id)
            case 'Collection': return this.GET_SINGLE_COLLECTION(id, { take : 100 })
            case 'PointOfInterest': return this.GET_SINGLE_POI(id)
            default: return null
        }
    }

    imageContentUrls (imageContent: ImageContent) {
        const image = imageContent.data.i18n.image?.value
        const url = smallerImageUrl(image)
        return url ? [url] : []
    }

    audioContentUrls (audioContent: AudioContent) {
        const audioUrl = audioContent.data.i18n.audio?.value.file.url
        return audioUrl ? [audioUrl]  : []
    }

    async getAudioAnswerUrls (answer: any) {
        if (answer.__typename !== 'MultipleChoiceAudioAnswer') return []
        return await answer.items.reduce(async (acc: string[], curr: any) => {
            const urlList = await acc
            const audioIds = curr.audioRefs.map((ref: I18nRef) => ref.id)
            const audioList: MediaAudio[] = await Promise.all(
                audioIds.map((id: string) => this.GET_SINGLE_AUDIO (id))
            )
            urlList.push(...audioList.map((audio: MediaAudio) => audio.file.url))
            return urlList
        }, Promise.resolve([]))
    }

    async questionContentUrls (questionContent: QuestionContent): Promise<string[]> {
        const urls: string[] = []
        const question = questionContent.data.question
        if (!question) return []
        const imageUrl = smallerImageUrl(question.image)
        if (imageUrl) urls.push(imageUrl)
        const audioAnswerUrls = await this.getAudioAnswerUrls(question.data.answer)
        if (audioAnswerUrls.length > 0) urls.push(...audioAnswerUrls)
        return urls
    }

    async poiContentUrls (content: Content): Promise<string[]> {
        switch (content.__typename) {
            case 'ImageContent': return this.imageContentUrls(content as ImageContent)
            case 'QuestionContent': return this.questionContentUrls(content as QuestionContent)
            case 'AudioContent': return this.audioContentUrls(content as AudioContent)
            default: return []
        }
    }

    poiAudioGuideUrls (audioGuideTrack: AudioGuideTrack): string[] {
        const audio = audioGuideTrack.i18n.audio
        if (!audio) return []
        const urls = [audio.value.file.url]
        const imageUrl = smallerImageUrl(audio.value.image)
        if (imageUrl) urls.push(imageUrl)
        return urls
    }

    reduceArray (arr: any[]): any[] {
        return arr.reduce((acc: string[], curr: string[]) => [...acc, ...curr], [])
    }

    async urlToFetch (item: cachableItemTypes): Promise<string[]> {
        switch (item?.__typename) {
            case 'Collection': 
                const  colHeaderUrl = smallerImageUrl(item.image)
                return colHeaderUrl ? [colHeaderUrl] : []
            case 'PointOfInterest': 
                const urls = []
                const poiHeaderUrl = smallerImageUrl(item.image)
                if (poiHeaderUrl) urls.push(poiHeaderUrl)
                const contentUrls = this.reduceArray(await Promise.all(item.contents.map(this.poiContentUrls)))
                if (contentUrls.length > 0) urls.push(...contentUrls)
                const audioGuideUrls = this.reduceArray(item.audioGuideTracks.map(this.poiAudioGuideUrls))
                if (audioGuideUrls.length) urls.push(...audioGuideUrls)
                return urls
            case 'MediaImage': 
                const imageHeaderUrl = smallerImageUrl(item)
                return imageHeaderUrl ? [imageHeaderUrl] : []
            case 'MediaVideo':
            case 'MediaAudio': return [item.file?.url ]
            default: return []
        }
    }

    async fetchListOfResource (resource: any) {
        if (resource.listLenght === 0) return []
        // console.log('fetchListOfResource' , resource)
        const data = await this.multipleItemQuery(resource.typename, resource.listLenght)
        // console.log('Data', data)
        if ( !data?.items) return []
        // @ts-ignore
        const mediaUrls = await Promise.all(data.items.map((item: cachableItemTypes) => {
            if (item?.__typename !== 'MediaAudio') return this.urlToFetch(item)
            else {
                const isUsedBy = item.usedBy?.filter((i: any) => i.info?.language === this.$i18n.locale).length > 0
                if (isUsedBy) return this.urlToFetch(item)
                return []
            }
        }))
        return this.reduceArray(mediaUrls) as string[]
    }

    async fetchAllResourcesIndependently (resource: any) {
        if (!resource.all) return []
        switch (resource.typename) {
            case 'MediaImage':
            case 'MediaVideo':
            case 'MediaAudio': 
                return this.fetchListOfResource({ ...resource, listLenght: -1 })
            case 'PointOfInterest': 
                return this.fetchSelectionOfResource({...resource, selection: await this.GET_ALL_POI_IDS() }) 
            case 'Collection':
                return this.fetchSelectionOfResource({...resource, selection: await this.GET_ALL_COLLECTION_IDS() })
            default: return []
        }
    }

    async fetchSelectionOfResource (resource: any) {
        if (resource.selection?.length === 0) return []
        const mediaUrls: string[] = []
        // fetch all items independently from selection ids array
        const getQuery = (id: string) => this.singleItemQuery(id, resource.typename)
        const items = await Promise.all(resource.selection.map(getQuery)) as cachableItemTypes[]
        const cachableItemUrls = await Promise.all(items.map((item: cachableItemTypes) => this.urlToFetch(item)))
        mediaUrls.push(...this.reduceArray(cachableItemUrls))
        // If Collection is fetched, fetch all poi inside
        const filterCollection = (item: cachableItemTypes) => item?.__typename === 'Collection'
        const collectionItems = items.filter(filterCollection) as Collection[]
        await Promise.all(collectionItems.map(async (item: Collection) => {
            // get list of poi ids
            const poiIds = item?.pointsOfInterest?.items.map((poi) => poi.id)
            const _resource = { typename: 'PointOfInterest', selection: poiIds }
            // Fetch list of collection's pois
            const poiMediaUrls = await this.fetchSelectionOfResource(_resource) 
            // Add pois' media urls
            mediaUrls.push(...poiMediaUrls)
        }))
        // Return the media urls to fetch
        return mediaUrls
    }

    async loadResources (resource: any) {
        // Initialize the media url list to fecth
        const _urlList: string[] = []
        // Fetch list of resources if resource.listLenght is not 0
        _urlList.push(...await this.fetchListOfResource(resource))
        // Fetch all resources independently
        _urlList.push(...await this.fetchAllResourcesIndependently(resource))
        // Fetch Selection of resources
        _urlList.push(...await this.fetchSelectionOfResource(resource))
        // return list of media url to fetch
        return _urlList
    }

    async startPreloadingData () {
        try {
            if (!this.preloadDataset) return
            console.info('[info][Preload] starting preloading data', this.preloadDataset)
            this.setPreloadStatus('apiDataPreloading')
            // Fetch all specific resources as needed and return a list of media urls to fetch
            const listOfUrlList = await Promise.all(this.preloadDataset.resources.map(this.loadResources)) as string[][]
            this.setPreloadStatus('apiDataPreloaded')
            const urlList = listOfUrlList.reduce((acc: string[], curr: string[]) => [...acc, ...curr], [])
            // filter duplicates urls
            this.urlList = urlList.filter((el, index) => urlList.indexOf(el) === index)
            console.info(`[info][Preload] Starting preloading ${this.urlList.length} media files`)
            const payload = this.urlList.map((url) => {
                return { url: addProtocol(url) }
            })
            // await this.fetchMediaList(urlList)
            this.setPreloadStatus('oldfileCleaning')
            const deletedFiles = await this.sendMessage({ kind: 'files', action: 'delete', payload })
            console.log('[info][Preload] List of deleted old files  ', deletedFiles)
            this.setPreloadStatus('filesPreloading')
            const cachedFiles: any = await this.sendMessage({ kind: 'files', action: 'preload', payload })
            console.log('[info][Preload] Liust of cached files cached', cachedFiles)
            this.setPreloadStatus('filesPreloaded')
            this.setPreloadStatus('done')
            this.setPreloadSuccess(true)
            // this.setPreloadError(true)
        } catch (error) {
            console.error('[info][Preload] Error during preloading', error)
            this.setPreloadError(true)
        }
        
    }

    async checkCachedFiles (urls: string[]) {
        const res = await this.sendMessage({
            kind: 'files',
            action: 'check',
            payload: urls.map((url) => {
                return { url: addProtocol(url) }
            })
        })
        return res
    }

    async deleteAllCachedFilesOtherThanList (urls: string[]) {
        const res = await this.sendMessage({
            kind: 'files',
            action: 'delete',
            payload: urls.map((url) => {
                return { url: addProtocol(url) }
            })
        })
        return res
    }

    sendMessage (message: any) {
        // This wraps the message posting/response in a promise, which will resolve if the response doesn't
        // contain an error, and reject with the error if it does. If you'd prefer, it's possible to call
        // controller.postMessage() and set up the onmessage handler independently of a promise, but this is
        // a convenient wrapper.
        return new Promise((resolve, reject) => {
          const messageChannel = new MessageChannel();
          messageChannel.port1.onmessage = (event) => {
            if (event.data.error) {
              reject(event.data.error);
            } else {
              resolve(event.data);
            }
          };
  
          // This sends the message data as well as transferring messageChannel.port2 to the service worker.
          // The service worker can then use the transferred port to reply via postMessage(), which
          // will in turn trigger the onmessage handler on messageChannel.port1.
          // See https://html.spec.whatwg.org/multipage/workers.html#dom-worker-postmessage
          if (!navigator?.serviceWorker?.controller) return resolve('no service worker')
          navigator.serviceWorker.controller.postMessage(message,
            [messageChannel.port2]);
        });
      }

    // async PRELOAD_COLLECTIONS (): Promise<string[]> {
    //     // alert('Prechargement des données en cache')
    //     const collections = await this.GET_ALL_COLLECTIONS({
    //         take: 100
    //       })
    //     if (collections?.itemsCount < 1) return []
    //     const collectionPromises: Array<Promise<Collection>> = []
    //     const pointOfInterestList: string[] = []
    //     const collectionImages: any[] = []
    //     const pointsOfInterestImages: any[] = []
    //     collections.items.forEach(({ id, pointsOfInterest, image }: Collection) => {
    //         collectionPromises.push(this.GET_SINGLE_COLLECTION(id))
    //         collectionPromises.push(this.GET_SINGLE_COLLECTION(id, { take: 100 }))
    //         collectionPromises.push(this.GET_SINGLE_COLLECTION(id, { take: 200 }))
    //         if (image?.formats?.length) {
    //             // collectionImages.push(image.formats[0].file.url)
    //             image.formats.forEach((i: ImageFormat) => {
    //                 collectionImages.push(i.file.url)
    //             })
    //             collectionImages.push(image.file.url)
    //         }
    //         pointsOfInterest.items.forEach(({ id, image }: PointOfInterest) => {
    //             pointOfInterestList.push(id)
    //             // if (image?.formats?.length) pointsOfInterestImages.push(image.formats[0].file.url)
    //             image?.formats?.forEach((i: ImageFormat) => {
    //                 pointsOfInterestImages.push(i.file.url)
    //             })
    //         })
    //     })
    //     await Promise.all(collectionPromises)
    //     pointOfInterestList.forEach(async (id: string) => {
    //         await this.GET_SINGLE_POI(id)
    //     })
    //     collectionImages.forEach((url: string) => {
    //         const img = new Image()
    //         img.src = addProtocol(url)
    //     })
    //     pointsOfInterestImages.forEach((url: string) => {
    //         const img = new Image()
    //         img.src = addProtocol(url)
    //     })
    //     // alert('Version offline prete')
    //     return []
    // }
}
